import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Skeleton,
  Space,
  Table,
  Typography,
  message,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { QueryOperation } from "../../code/search-query/search-query";
import { nameOf } from "../../utils/typescript";
import {
  deleteManager,
  getCompanyManagers,
  getCompanyUserRoles,
  getCompanyDepartments,
} from "../../repository/data.repository";
import { registerCompanyUser } from "../../repository/auth.repository";
import {
  Company,
  CompanyRole,
  Department,
  ManagerUser,
  MAX_PAGE_SIZE,
} from "../../repository/data.default";
import { retry, getProblemMessage } from "../../utils/http";
import ComboBox from "../../controls/ComboBox";
import PhoneInput, { phoneRule } from "../../controls/PhoneInput";
import { useSelector } from "../../store/store";
import { authorizeSelectors } from "../../store/authorize";
import CompanyUserPreviewModal from "./CompanyUserPreviewModal";
import { DATE_DISPLAY_FORMAT } from "../../utils/date";

export interface CompanyManagersPanelProps {
  readonly open: boolean;
  readonly companyUid: string | undefined;
  readonly companyName?: string;
  readonly reloadToken?: string | number;
  readonly onChange?: () => void;
  readonly onCountLoaded?: (count: number) => void;
  readonly scopeCompanies: Company[];
}

function CompanyManagersPanel(props: CompanyManagersPanelProps) {
  const permissions = useSelector(authorizeSelectors.permissions);
  const canCreate = permissions.can("CompanyManagers", "create");
  const canDelete = permissions.can("CompanyManagers", "delete");
  const myUid = (permissions.payload as any)?.sub as string | undefined;

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ManagerUser[]>([]);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [preview, setPreview] = useState<ManagerUser | null>(null);
  const everLoadedRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scopeUids = useMemo(
    () => props.scopeCompanies.map((c) => c.uid),
    [props.scopeCompanies],
  );
  const scopeKey = scopeUids.join("|");

  useEffect(() => {
    if (!props.open || scopeUids.length === 0) return;
    load(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, scopeKey, props.reloadToken]);

  useEffect(() => {
    if (!props.open || scopeUids.length === 0) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(search), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  function load(nameFilter: string): void {
    if (scopeUids.length === 0) return;
    setLoading(true);
    const conditions: any[] = [
      {
        field: nameOf<ManagerUser>("companyUid"),
        values: scopeUids,
        operation: QueryOperation.In,
      },
    ];
    if (nameFilter.trim()) {
      conditions.push({
        field: nameOf<ManagerUser>("firstName"),
        values: [nameFilter.trim()],
        operation: QueryOperation.StartsWith,
      });
    }
    retry(() =>
      getCompanyManagers({
        searchQuery: { conditions },
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
      }),
    )
      .then(({ data }) => {
        setItems(data);
        setLoading(false);
        everLoadedRef.current = true;
        props.onCountLoaded?.(data.length);
      })
      .catch(() => setLoading(false));
  }

  function onMutated(): void {
    setAddOpen(false);
    load(search);
    props.onChange?.();
  }

  async function onDelete(uid: string): Promise<void> {
    try {
      await deleteManager(uid);
      message.success("Manager deleted.");
      onMutated();
    } catch (err) {
      message.error(getProblemMessage(err));
    }
  }

  if (!props.open) return null;

  const showSkeleton = loading && !everLoadedRef.current;
  const multiCompany = props.scopeCompanies.length > 1;

  return (
    <Card
      size="small"
      title={
        <Space>
          <Typography.Text strong>
            {multiCompany ? "Managers (whole branch)" : "Managers"}
          </Typography.Text>
          <Typography.Text type="secondary">({items.length})</Typography.Text>
        </Space>
      }
      extra={
        <Space>
          <Input.Search
            placeholder="Search by first name…"
            allowClear
            size="small"
            style={{ width: 200 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button
            type="primary"
            size="small"
            disabled={!canCreate || !props.companyUid}
            onClick={() => setAddOpen(true)}
          >
            + Register Manager
          </Button>
        </Space>
      }
    >
      {showSkeleton ? (
        <Skeleton active paragraph={{ rows: 3 }} title={false} />
      ) : (
        <Table
          size="small"
          loading={loading}
          rowKey={(r) => r.uid}
          dataSource={items}
          pagination={items.length > 10 ? { pageSize: 10 } : false}
          locale={{ emptyText: "No managers yet." }}
          onRow={(record) => ({
            onClick: () => setPreview(record),
            style: { cursor: "pointer" },
          })}
          columns={[
            { title: "Name", key: "name", render: (_, r) => `${r.firstName ?? ""} ${r.lastName ?? ""}`.trim() },
            ...(multiCompany
              ? [{ title: "Company", dataIndex: "companyName", key: "companyName" }]
              : []),
            { title: "Role", dataIndex: "roleName", key: "roleName" },
            { title: "Department", dataIndex: "departmentName", key: "departmentName" },
            { title: "Email", dataIndex: "email", key: "email" },
            { title: "Phone", dataIndex: "phoneNumber", key: "phoneNumber" },
            {
              title: "",
              key: "actions",
              width: 90,
              render: (_: any, record: ManagerUser) => {
                const isSelf = myUid && record.uid === myUid;
                return (
                  <Popconfirm
                    title="Delete this manager?"
                    description="This permanently removes the user account."
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    disabled={!canDelete || !!isSelf}
                    onConfirm={(e) => {
                      e?.stopPropagation();
                      onDelete(record.uid);
                    }}
                    onCancel={(e) => e?.stopPropagation()}
                  >
                    <Button
                      size="small"
                      type="link"
                      danger
                      disabled={!canDelete || !!isSelf}
                      onClick={(e) => e.stopPropagation()}
                      title={isSelf ? "You cannot delete your own account" : undefined}
                    >
                      Delete
                    </Button>
                  </Popconfirm>
                );
              },
            },
          ]}
        />
      )}
      <CompanyUserPreviewModal
        open={!!preview}
        kind="manager"
        user={preview}
        onClose={() => setPreview(null)}
      />
      <RegisterManagerDialog
        open={addOpen}
        scopeCompanies={props.scopeCompanies}
        defaultCompanyUid={props.companyUid}
        onCancel={() => setAddOpen(false)}
        onFinish={onMutated}
      />
    </Card>
  );
}

function RegisterManagerDialog(props: {
  open: boolean;
  scopeCompanies: Company[];
  defaultCompanyUid: string | undefined;
  onCancel: () => void;
  onFinish: () => void;
}) {
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (!props.open) {
      form.resetFields();
      setError("");
    } else {
      const def = props.scopeCompanies.find((c) => c.uid === props.defaultCompanyUid);
      if (def) form.setFieldsValue({ company: { display: def.name, value: def.uid } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  // Chosen target company drives the scope of the Role / Department pickers.
  const chosenCompanyUid: string | undefined = values?.company?.value;

  const companyRequest = (search: string) => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? props.scopeCompanies.filter((c) => c.name.toLowerCase().startsWith(q))
      : props.scopeCompanies;
    return Promise.resolve(
      filtered.map((c) => ({ display: c.name, value: c.uid, dataItem: c })),
    );
  };

  const rolesRequest = (search: string) => {
    if (!chosenCompanyUid) return Promise.resolve([]);
    return retry(() =>
      getCompanyUserRoles({
        searchQuery: {
          conditions: [
            { field: nameOf<CompanyRole>("companyUid"), values: [chosenCompanyUid], operation: QueryOperation.Equal },
            { field: nameOf<CompanyRole>("isManager"), values: ["true"], operation: QueryOperation.Equal },
            { field: nameOf<CompanyRole>("name"), values: [search], operation: QueryOperation.StartsWith },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) => data.map((d) => ({ display: d.name, value: d.uid, dataItem: d })));
  };

  const departmentsRequest = (search: string) => {
    if (!chosenCompanyUid) return Promise.resolve([]);
    return retry(() =>
      getCompanyDepartments({
        searchQuery: {
          conditions: [
            { field: nameOf<Department>("companyUid"), values: [chosenCompanyUid], operation: QueryOperation.Equal },
            { field: nameOf<Department>("name"), values: [search], operation: QueryOperation.StartsWith },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) => data.map((d) => ({ display: d.name, value: d.uid, dataItem: d })));
  };

  async function onSubmit(): Promise<void> {
    try {
      const vs = await form.validateFields();
      setSaving(true);
      setError("");
      const dob: Dayjs | undefined = vs.dateOfBirth;
      await registerCompanyUser({
        companyUid: vs.company?.value,
        usersInfos: [
          {
            userName: vs.userName || undefined,
            email: vs.email,
            firstName: vs.firstName,
            middleName: vs.middleName || undefined,
            lastName: vs.lastName,
            dateOfBirth: dob ? dob.format("YYYY-MM-DD") : (undefined as any),
            roleUid: vs.role?.value,
            departmentUid: vs.department?.value,
            phoneNumber: vs.phoneNumber || undefined,
            companyPhoneNumber: vs.companyPhoneNumber || undefined,
          } as any,
        ],
      } as any);
      message.success(
        `Manager "${vs.firstName} ${vs.lastName}" registered for "${vs.company?.display}". Activation email sent.`,
      );
      setSaving(false);
      props.onFinish();
    } catch (err: any) {
      setSaving(false);
      if (err?.errorFields) return;
      setError(getProblemMessage(err));
    }
  }

  return (
    <Modal
      open={props.open}
      title="Register Manager"
      onCancel={props.onCancel}
      onOk={onSubmit}
      okText="Register"
      confirmLoading={saving}
      destroyOnClose
      width={640}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="company"
          label="Register for (company)"
          rules={[{ required: true, message: "Company is required" }]}
        >
          <ComboBox request={companyRequest} />
        </Form.Item>
        <Form.Item name="userName" label="User Name (optional)">
          <Input />
        </Form.Item>
        <Form.Item name="firstName" label="First Name" rules={[{ required: true, message: "First name is required" }]}>
          <Input />
        </Form.Item>
        <Form.Item name="middleName" label="Middle Name (optional)">
          <Input />
        </Form.Item>
        <Form.Item name="lastName" label="Last Name" rules={[{ required: true, message: "Last name is required" }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: "Email is required" },
            { type: "email", message: "Invalid email format" },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="phoneNumber" label="Phone Number (optional)" rules={[phoneRule]}>
          <PhoneInput />
        </Form.Item>
        <Form.Item name="companyPhoneNumber" label="Company Phone (optional)" rules={[phoneRule]}>
          <PhoneInput />
        </Form.Item>
        <Form.Item
          name="dateOfBirth"
          label="Date of Birth"
          rules={[
            { required: true, message: "Date of birth is required" },
            {
              validator: (_r, v: Dayjs) => {
                if (!v) return Promise.resolve();
                if (v.isAfter(dayjs().endOf("day")))
                  return Promise.reject(new Error("Date of Birth cannot be in the future"));
                if (v.isBefore(dayjs("1900-01-01")))
                  return Promise.reject(new Error("Date of Birth cannot be before 1900"));
                return Promise.resolve();
              },
            },
          ]}
        >
          <DatePicker
            className="w-full"
            format={DATE_DISPLAY_FORMAT}
            disabledDate={(c) =>
              !!c && (c.isAfter(dayjs().endOf("day")) || c.isBefore(dayjs("1900-01-01")))
            }
          />
        </Form.Item>
        <Form.Item
          name="role"
          label="Role (manager-level, scoped to selected company)"
          rules={[{ required: true, message: "Role is required" }]}
        >
          <ComboBox request={rolesRequest} />
        </Form.Item>
        <Form.Item
          name="department"
          label="Department (scoped to selected company)"
          rules={[{ required: true, message: "Department is required" }]}
        >
          <ComboBox request={departmentsRequest} />
        </Form.Item>
      </Form>
      {error && (
        <Typography.Text type="danger" className="mt-2 block">
          {error}
        </Typography.Text>
      )}
    </Modal>
  );
}

export default CompanyManagersPanel;
